import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../../Common/isRequestCanceled";
import {
  BASE_URL,
  REVENUE_CHART,
  REPORTS_BASE_URL,
  REPORTS_REVENUE_DATA_API,
  SALES_BY_HOURS_REPORT_DASHBOARD,
  IS_PY_GRAPHS,
} from "../../../../Constants/Config";
import { useAuthDetails } from "../../../../Common/cookiesHelper";

const initialState = {
  loading: true,
  revenueData: [],
  totalRevenue: 0,
  previousDayRevenue: 0,
  previousWeekRevenue: 0,
  error: "",
  isCancel: false,
};

let controller = null;

export const getRevenue = createAsyncThunk(
  "revenueChart/getRevenue.",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();

    if (controller) {
      controller.abort();
    }

    controller = new AbortController();

    try {
      const { token, ...otherUserData } = userTypeData;

      const payload = { ...data };
      const activeType =
        payload?.activeType || payload?.view_by || payload?.date_type;

      const isDay = String(activeType || "").toLowerCase() === "day";
      const isWeek = String(activeType || "").toLowerCase() === "week";
      const isMonth = String(activeType || "").toLowerCase() === "month";

      const dateInYYYYMMDD = (date) => {
        const year = date?.getFullYear();
        const month = String(date?.getMonth() + 1)?.padStart(2, "0");
        const day = String(date?.getDate())?.padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const parseLocalYMD = (ymd) => {
        const parts = String(ymd || "").split("-");
        if (parts.length !== 3) return null;
        const y = Number(parts[0]);
        const m = Number(parts[1]);
        const d = Number(parts[2]);
        if (!y || !m || !d) return null;
        return new Date(y, m - 1, d);
      };

      const getLocalTodayYMD = () => {
        const now = new Date();
        return dateInYYYYMMDD(
          new Date(now.getFullYear(), now.getMonth(), now.getDate())
        );
      };

      const getWeekStartMonday = (date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const day = d.getDay(); // 0=Sun
        const diff = day === 0 ? -6 : 1 - day;
        d.setDate(d.getDate() + diff);
        d.setHours(0, 0, 0, 0);
        return d;
      };

      // ===== WEEK LOGIC =====
      if (isWeek) {
        const shouldUseToday = payload?.isViewingCurrentPeriod === true;
        const anchorYMD = shouldUseToday
          ? getLocalTodayYMD()
          : (payload?.end_date || payload?.start_date);
        const anchorDate = parseLocalYMD(anchorYMD) || new Date();

        const weekStart = getWeekStartMonday(anchorDate);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const result = [];
        for (let i = 0; i < 7; i++) {
          const d = new Date(weekStart);
          d.setDate(d.getDate() + i);
          const formatted = dateInYYYYMMDD(d);
          result.push(`${formatted}_${formatted}`);
        }

        if (shouldUseToday) {
          payload.start_date = dateInYYYYMMDD(weekStart);
          payload.end_date = dateInYYYYMMDD(weekEnd);
        } else {
          payload.start_date = dateInYYYYMMDD(weekStart);
          payload.end_date = dateInYYYYMMDD(weekEnd);
        }

        payload.date_range = result.join(",");
        delete payload.new_date_range;
      }

      // ===== MONTH LOGIC (12 MONTHS) =====
      if (isMonth) {
        const buildMonthRanges = (anchorInput, monthsCount = 12) => {
          const anchorDate = (() => {
            if (!anchorInput) return new Date();
            const parsed = new Date(anchorInput);
            return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
          })();

          const ranges = [];
          for (let i = monthsCount - 1; i >= 0; i--) {
            const startDate = new Date(
              anchorDate.getFullYear(),
              anchorDate.getMonth() - i,
              1
            );
            const endDate = new Date(
              anchorDate.getFullYear(),
              anchorDate.getMonth() - i + 1,
              0
            );
            ranges.push(
              `${dateInYYYYMMDD(startDate)}_${dateInYYYYMMDD(endDate)}`
            );
          }
          return ranges;
        };

        const monthRanges = buildMonthRanges(payload?.end_date, 12);
        if (monthRanges.length) {
          const firstRangeParts = monthRanges[0]?.split("_") || [];
          const lastRangeParts =
            monthRanges[monthRanges.length - 1]?.split("_") || [];

          if (firstRangeParts[0]) {
            payload.start_date = firstRangeParts[0];
          }
          if (lastRangeParts[1] || lastRangeParts[0]) {
            payload.end_date = lastRangeParts[1] || lastRangeParts[0];
          }

          payload["date_range"] = monthRanges.join(",");
        }
      }

      if (isDay) {
        const dayApiUrl = REPORTS_BASE_URL + SALES_BY_HOURS_REPORT_DASHBOARD;
        const reportsApiUrl = REPORTS_BASE_URL + REPORTS_REVENUE_DATA_API;

        const [dayResponse, reportsResponse] = await Promise.all([
          axios.post(
            dayApiUrl,
            { ...payload, ...otherUserData },
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
              },
              signal: controller.signal,
            }
          ),
          axios.post(
            reportsApiUrl,
            { ...payload, ...otherUserData },
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
              },
              signal: controller.signal,
            }
          ),
        ]);

        return {
          ...(dayResponse?.data || {}),
          reports_revenue_data: reportsResponse?.data,
        };
      }

      const apiUrl = isWeek || isMonth
        ? REPORTS_BASE_URL + REPORTS_REVENUE_DATA_API
        : IS_PY_GRAPHS === true || IS_PY_GRAPHS === "true"
          ? REPORTS_BASE_URL + REPORTS_REVENUE_DATA_API
          : BASE_URL + REVENUE_CHART;

      const response = await axios.post(
        apiUrl,
        { ...payload, ...otherUserData },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        }
      );

      // ===== WEEK RESPONSE PROCESSING =====
      if (isWeek) {
        const resultArray = response?.data?.filter_revenue_data || [];
        const dateKeys = data?.date_range
          ? String(data.date_range)
            .split(",")
            .map((range) => (range ? String(range).split("_")[0] : ""))
            .filter(Boolean)
          : data?.start_date
            ? [String(data.start_date)]
            : [];

        let total = 0;
        if (Array.isArray(resultArray)) {
          resultArray.forEach((row) => {
            total += +row?.total_revenue || 0;
          });
        }

        const filter_revenue_data = dateKeys.map(() => ({
          total_revenue: parseFloat((total || 0).toFixed(2)),
          sale_count: 0,
        }));
        const total_revenue_data = total;

        const bucketCount = dateKeys.length || 7;
        const byDayCount = new Map();
        if (Array.isArray(resultArray)) {
          resultArray.forEach((row) => {
            const idx = Number.parseInt(row?.day_count, 10);
            if (Number.isFinite(idx)) {
              byDayCount.set(idx, row);
            }
          });
        }

        const week_filter_revenue_data = Array.from({ length: bucketCount }).map(
          (_, idx) => {
            const row = byDayCount.get(idx);
            const revenue = row ? Number(row?.total_revenue || 0) : 0;
            const sales = row ? Number(row?.sale_count || 0) : 0;
            return {
              day_count: idx,
              total_revenue: parseFloat((revenue || 0).toFixed(2)),
              sale_count: sales,
            };
          }
        );

        return {
          ...response.data,
          filter_revenue_data,
          week_filter_revenue_data,
          total_revenue_data: parseFloat(total_revenue_data.toFixed(2)),
        };
      }

      // ===== MONTH RESPONSE PROCESSING =====
      if (isMonth) {
        // For Month view, return the response as-is
        // Backend now returns 12 items in filter_revenue_data
        return response.data;
      }

      return response.data;
    } catch (error) {
      if (axios.isCancel(error) || error.name === "CanceledError") {
        return rejectWithValue({ isCanceled: true });
      }
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const RevenueChartSlice = createSlice({
  name: "revenueChart",
  initialState,
  reducers: {
    clearRevenueChart: (state, action) => {
      state.loading = true;
      state.revenueData = [];
      state.weekRevenueData = [];
      state.totalRevenue = 0;
      state.previousDayRevenue = 0;
      state.previousWeekRevenue = 0;
      state.error = "";
      state.isCancel = false;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getRevenue.pending, (state) => {
      state.loading = true;
      state.isCancel = false;
    });
    builder.addCase(getRevenue.fulfilled, (state, action) => {
      state.loading = false;
      state.revenueData = action.payload?.filter_revenue_data || [];
      state.weekRevenueData = action.payload?.week_filter_revenue_data || [];

      const reportsRevenueData = action.payload?.reports_revenue_data;
      const requestActiveType = String(
        action.meta?.arg?.activeType ||
        action.meta?.arg?.view_by ||
        action.meta?.arg?.date_type ||
        ""
      ).toLowerCase();

      const reportsSeries = Array.isArray(reportsRevenueData?.filter_revenue_data)
        ? reportsRevenueData.filter_revenue_data
        : [];
      const reportsLast = reportsSeries.length
        ? reportsSeries[reportsSeries.length - 1]
        : null;
      const reportsPrev = reportsSeries.length > 1
        ? reportsSeries[reportsSeries.length - 2]
        : null;

      state.totalRevenue =
        requestActiveType === "day" && reportsLast?.total_revenue != null
          ? Number(reportsLast.total_revenue) || 0
          : action.payload?.total_revenue_data || 0;

      state.previousDayRevenue =
        requestActiveType === "day"
          ? (reportsPrev?.total_revenue != null
            ? Number(reportsPrev.total_revenue) || 0
            : action.payload?.previous_day_total_revenue_data || 0)
          : 0;

      state.previousWeekRevenue =
        requestActiveType === "week" ? action.payload?.previous_revenue_data || 0 : 0;
      state.error = "";
      state.isCancel = false;
    });
    builder.addCase(getRevenue.rejected, (state, action) => {
      state.loading = false;
      state.revenueData = [];
      state.weekRevenueData = [];
      state.totalRevenue = 0;
      state.previousDayRevenue = 0;
      state.previousWeekRevenue = 0;
      state.error = action.error.message;
      state.isCancel = action.payload?.isCanceled || false;
    });
  },
});

export const { clearRevenueChart } = RevenueChartSlice.actions;
export default RevenueChartSlice.reducer;